<template>
  <v-container fluid class="pa-6">
    <!-- KPI Cards Section -->
    <v-row class="mb-4 mb-md-6">
      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="primary">mdi-store-search</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Tiendas</div>
              <div class="kpi-value">{{ stores.length }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                <v-icon x-small color="success">mdi-check-circle</v-icon>
                Disponibles
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="success">mdi-package-variant</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Productos</div>
              <div class="kpi-value">{{ formatNumber(items.length) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                <v-icon x-small color="info">mdi-filter</v-icon>
                Encontrados
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="warning">mdi-cube-outline</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Total Stock</div>
              <div class="kpi-value">{{ formatNumber(totalStock) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">Unidades disponibles</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="info">mdi-tag-multiple</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Valor Total</div>
              <div class="kpi-value">{{ formatCurrency(totalValue) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">En inventario</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Content Card -->
    <v-card class="main-card elevation-0" outlined>
      <!-- Header -->
      <v-card-title class="pa-0">
        <v-toolbar flat class="minimal-toolbar">
          <h2 class="section-title" :class="{ 'text-h6': $vuetify.breakpoint.mobile }">
            Consulta en otras tiendas
          </h2>
          <v-spacer></v-spacer>
          <v-btn
            v-if="items.length > 0"
            color="primary"
            text
            @click="showChart = !showChart"
          >
            <v-icon left>{{ showChart ? "mdi-table" : "mdi-chart-bar" }}</v-icon>
            {{ showChart ? "Ver Tabla" : "Ver Gráfico" }}
          </v-btn>
        </v-toolbar>
      </v-card-title>

      <!-- Search Form -->
      <v-form v-model="searchvalid" ref="searchform" @submit="get_report">
        <v-card-text class="py-4">
          <v-row>
            <v-col cols="12" md="3">
              <v-select
                v-model="brand"
                :items="brands"
                item-value="codigo"
                item-text="valor"
                label="Marca"
                clearable
                hide-details="auto"
                outlined
                dense
                :rules="f_required"
                prepend-inner-icon="mdi-tag"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="refe"
                label="Referencia"
                clearable
                hide-details="auto"
                outlined
                dense
                required
                :rules="f_required"
                prepend-inner-icon="mdi-barcode"
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-autocomplete
                v-model="selectedColor"
                :items="availableColors"
                label="Color"
                clearable
                hide-details="auto"
                outlined
                dense
                @change="applyFilters"
                prepend-inner-icon="mdi-palette"
                :disabled="items.length === 0"
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-autocomplete
                v-model="selectedSize"
                :items="availableSizes"
                label="Talla"
                clearable
                hide-details="auto"
                outlined
                dense
                @change="applyFilters"
                prepend-inner-icon="mdi-ruler"
                :disabled="items.length === 0"
              />
            </v-col>
            <v-col cols="12" md="2" class="d-flex align-center">
              <v-btn
                color="primary"
                type="submit"
                :loading="loading_status"
                :disabled="!searchvalid"
                block
              >
                <v-icon left>mdi-magnify</v-icon>
                Buscar
              </v-btn>
            </v-col>
          </v-row>

          <!-- Quick Search -->
          <v-row class="mt-4" v-if="items.length > 0">
            <v-col cols="12">
              <v-text-field
                v-model="search"
                label="Búsqueda rápida en resultados"
                clearable
                hide-details="auto"
                outlined
                dense
                prepend-inner-icon="mdi-magnify"
                placeholder="Buscar por tienda, categoría, marca..."
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-form>

      <v-divider v-if="items.length > 0"></v-divider>

      <!-- Results Section -->
      <template v-if="!showChart">
        <!-- Desktop Table -->
        <template v-if="$vuetify.breakpoint.mdAndUp">
          <v-data-table
            :headers="headers"
            :items="items"
            :items-per-page="10"
            item-key="id"
            sort-by="cantidad"
            sort-desc
            class="minimal-table"
            :loading="loading_status"
            loader-height="3"
            loading-text="Cargando..."
            :search="search"
            :footer-props="{
              'items-per-page-options': [10, 25, 50, 100],
            }"
          >
            <!-- Store Column -->
            <template v-slot:[`item.pos`]="{ item }">
              <div class="store-info">
                <v-icon small class="mr-2" color="primary">mdi-store</v-icon>
                <span class="font-weight-medium">{{ item.pos }}</span>
              </div>
            </template>

            <!-- Product Info -->
            <template v-slot:[`item.product`]="{ item }">
              <div class="product-info">
                <div class="product-main">
                  <span class="font-weight-medium">{{ item.refe }}</span>
                  <v-chip x-small label class="ml-2">{{ item.brand }}</v-chip>
                </div>
                <div class="product-details text-caption grey--text">
                  {{ item.category }} • {{ item.groupName }}
                </div>
              </div>
            </template>

            <!-- Color/Size -->
            <template v-slot:[`item.variant`]="{ item }">
              <div class="variant-info">
                <v-chip small label class="mr-1 mb-1">
                  <v-icon x-small left>mdi-palette</v-icon>
                  {{ item.colorName }}
                </v-chip>
                <v-chip small label outlined>
                  <v-icon x-small left>mdi-ruler</v-icon>
                  {{ item.talla }}
                </v-chip>
              </div>
            </template>

            <!-- Price -->
            <template v-slot:[`item.precio`]="{ item }">
              <div class="text-right font-weight-medium">
                {{ formatCurrency(item.precio) }}
              </div>
            </template>

            <!-- Discount -->
            <template v-slot:[`item.descuento`]="{ item }">
              <div class="text-right">
                <v-chip
                  x-small
                  :color="item.descuento > 0 ? 'error' : 'grey'"
                  :text-color="item.descuento > 0 ? 'white' : 'black'"
                  label
                >
                  <span v-if="parseFloat(item.descuento) < 100">
                    {{ parseFloat(item.descuento).toFixed(0) }}%
                  </span>
                  <span v-else>
                    {{ formatCurrency(item.descuento) }}
                  </span>
                </v-chip>
              </div>
            </template>

            <!-- Quantity -->
            <template v-slot:[`item.cantidad`]="{ item }">
              <div class="quantity-info text-right">
                <span
                  class="font-weight-bold"
                  :class="{
                    'success--text': item.cantidad > 10,
                    'warning--text': item.cantidad > 0 && item.cantidad <= 10,
                    'error--text': item.cantidad === 0,
                  }"
                >
                  {{ formatNumber(item.cantidad) }}
                </span>
              </div>
            </template>

            <!-- Footer with totals -->
            <template v-slot:body.append v-if="items.length > 0">
              <tr class="grey lighten-4">
                <td colspan="5" class="text-right font-weight-bold">TOTALES</td>
                <td class="text-right font-weight-bold">
                  {{ formatNumber(totalStock) }} und
                </td>
              </tr>
            </template>
          </v-data-table>
        </template>

        <!-- Mobile List -->
        <template v-else>
          <v-list v-if="items.length > 0" class="py-0">
            <template v-for="(item, index) in filteredItems">
              <v-list-item :key="item.id" class="px-4 py-3">
                <v-list-item-content>
                  <!-- Store and Product -->
                  <div class="d-flex justify-space-between align-start mb-2">
                    <div>
                      <div class="font-weight-medium d-flex align-center">
                        <v-icon small class="mr-1" color="primary">mdi-store</v-icon>
                        {{ item.pos }}
                      </div>
                      <div class="text-body-2 mt-1">
                        {{ item.refe }} - {{ item.brand }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ item.category }}
                      </div>
                    </div>
                    <v-chip
                      small
                      :color="
                        item.cantidad > 10
                          ? 'success'
                          : item.cantidad > 0
                          ? 'warning'
                          : 'error'
                      "
                      text-color="white"
                      label
                    >
                      {{ item.cantidad }} und
                    </v-chip>
                  </div>

                  <!-- Variant Info -->
                  <div class="mb-2">
                    <v-chip x-small label class="mr-1">
                      {{ item.colorName }}
                    </v-chip>
                    <v-chip x-small label outlined> Talla {{ item.talla }} </v-chip>
                  </div>

                  <!-- Price Info -->
                  <v-row no-gutters>
                    <v-col cols="6">
                      <div class="text-caption grey--text">Precio</div>
                      <div class="font-weight-medium">
                        {{ formatCurrency(item.precio) }}
                      </div>
                    </v-col>
                    <v-col cols="6" class="text-right">
                      <div class="text-caption grey--text">Descuento</div>
                      <div
                        class="font-weight-medium error--text"
                        v-if="item.descuento > 0"
                      >
                        <span v-if="parseFloat(item.descuento) < 100">
                          {{ parseFloat(item.descuento).toFixed(0) }}%
                        </span>
                        <span v-else>
                          {{ formatCurrency(item.descuento) }}
                        </span>
                      </div>
                      <div v-else>-</div>
                    </v-col>
                  </v-row>
                </v-list-item-content>
              </v-list-item>
              <v-divider
                v-if="index < filteredItems.length - 1"
                :key="`div-${item.id}`"
              />
            </template>

            <!-- Mobile Totals -->
            <v-list-item class="grey lighten-4 py-3">
              <v-list-item-content>
                <div class="font-weight-bold text-center mb-2">TOTALES</div>
                <v-row no-gutters>
                  <v-col cols="6">
                    <div class="text-caption">Total Stock</div>
                    <div class="font-weight-bold">{{ formatNumber(totalStock) }} und</div>
                  </v-col>
                  <v-col cols="6" class="text-right">
                    <div class="text-caption">Valor Total</div>
                    <div class="font-weight-bold">{{ formatCurrency(totalValue) }}</div>
                  </v-col>
                </v-row>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <!-- Empty State -->
          <div v-else-if="!loading_status" class="text-center py-12">
            <v-icon size="64" color="grey lighten-2">mdi-store-search-outline</v-icon>
            <h3 class="text-h6 mt-4 grey--text">No se encontraron productos</h3>
            <p class="grey--text mt-2">Intenta ajustar los filtros de búsqueda</p>
          </div>
        </template>
      </template>

      <!-- Chart View -->
      <v-card-text v-else class="py-6">
        <GChart
          type="ColumnChart"
          :data="chartData"
          :options="chartOptions"
          class="chart-container"
        />
      </v-card-text>
    </v-card>

    <!-- Loading Dialog -->
    <v-dialog v-model="loading_status" persistent width="300">
      <v-card>
        <v-card-text class="pa-6">
          <div class="text-center">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
              class="mb-4"
            ></v-progress-circular>
            <h3 class="text-h6 mb-2">Buscando productos</h3>
            <p class="text-body-2 grey--text mb-0">{{ lStatus }}</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver, getToday, getdays_ago } from "@/services/webserver.js";
import { GChart } from "vue-google-charts";
import createDoc from "@/utils/create_doc.js";

export default {
  components: { GChart },
  data() {
    return {
      // UI State
      loading_status: false,
      showChart: false,
      searchvalid: false,
      search: null,
      lStatus: null,

      // Snackbar
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },

      // Headers modernizados
      headers: [
        {
          text: "Tienda",
          align: "start",
          sortable: true,
          filterable: true,
          value: "pos",
          width: "15%",
        },
        {
          text: "Producto",
          align: "start",
          sortable: false,
          value: "product",
          width: "30%",
        },
        {
          text: "Variante",
          align: "start",
          sortable: false,
          value: "variant",
          width: "20%",
        },
        {
          text: "Precio",
          align: "end",
          sortable: true,
          value: "precio",
          width: "12%",
        },
        {
          text: "Desc.",
          align: "end",
          sortable: true,
          value: "descuento",
          width: "10%",
        },
        {
          text: "Stock",
          align: "end",
          sortable: true,
          value: "cantidad",
          width: "13%",
        },
      ],

      // Data
      items: [],
      originalItems: [],
      dialog: false,
      item: createDoc(),
      dateFrom: getdays_ago(-30),
      dateTo: getToday(),

      // Chart
      chartData: null,
      chartOptions: {
        curveType: "function",
        legend: { position: "right" },
        interpolateNulls: true,
        crosshair: { trigger: "both", opacity: 0.95 },
        title: "Distribución de Stock por Tienda",
        isStacked: false,
        hAxis: {
          title: "Tiendas",
          slantedText: true,
          slantedTextAngle: 15,
        },
        vAxis: {
          title: "Cantidad",
        },
        colors: ["#1976D2", "#388E3C", "#F57C00", "#D32F2F"],
        height: 400,
        chartArea: { width: "70%", height: "70%" },
        animation: {
          startup: true,
          duration: 1000,
          easing: "out",
        },
      },

      // Filters
      brands: [],
      brand: null,
      categories: [],
      category: null,
      colors: [],
      color: null,
      refe: null,
      f_required: [(v) => !!v || "Campo requerido"],
      stores: [],
      selectedColor: null,
      selectedSize: null,
      availableColors: [],
      availableSizes: [],
    };
  },

  computed: {
    totalStock() {
      return this.items.reduce((sum, item) => sum + (parseFloat(item.cantidad) || 0), 0);
    },

    totalValue() {
      return this.items.reduce((sum, item) => {
        const price = parseFloat(item.precio) || 0;
        const qty = parseFloat(item.cantidad) || 0;
        const discount = parseFloat(item.descuento) || 0;
        const discountValue = discount < 100 ? (price * discount) / 100 : discount;
        return sum + (price - discountValue) * qty;
      }, 0);
    },

    filteredItems() {
      if (!this.search) return this.items;
      return this.items.filter((item) => {
        const searchLower = this.search.toLowerCase();
        return Object.values(item).some(
          (value) => value && value.toString().toLowerCase().includes(searchLower)
        );
      });
    },
  },

  mounted() {
    this.categories = window.settings["CATEGORIA"].sort(this.GetSortOrder("valor"));
    this.brands = window.settings["MARCA"].sort(this.GetSortOrder("valor"));
    this.colors = window.settings["COLOR"].sort(this.GetSortOrder("valor"));
    this.get_stores();
  },

  methods: {
    // Format currency
    formatCurrency(value) {
      if (!value && value !== 0) return "$0";
      return (
        "$" +
        parseFloat(value).toLocaleString("es", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      );
    },

    // Format number
    formatNumber(value) {
      if (!value && value !== 0) return "0";
      return parseFloat(value).toLocaleString("es", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },

    // Show message
    showMessage(text, color = "info", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout,
      };
    },

    sumTable(table, key) {
      return parseFloat(
        this[table].reduce((a, b) => a + (parseFloat(b[key]) || 0), 0)
      ).toLocaleString(2);
    },

    async get_report(e) {
      if (e) e.preventDefault();

      this.$refs.searchform.validate();
      if (!this.searchvalid) return;

      this.loading_status = true;
      this.items = [];
      this.originalItems = [];
      this.selectedColor = null;
      this.selectedSize = null;
      this.availableColors = [];
      this.availableSizes = [];

      var response = [];
      var store = "";

      try {
        for (store of this.stores) {
          this.lStatus = store.store_name;

          var filters = [
            {
              field: "id_pos",
              operator: "=",
              value: store.store_id,
            },
            {
              field: "cantidad",
              operator: ">",
              value: 0,
            },
          ];

          if (this.brand) {
            filters.push({
              field: "marca",
              operator: "=",
              value: this.brand,
            });
          }

          if (this.refe) {
            filters.push({
              field: "refe",
              operator: " LIKE ",
              value: this.refe,
            });
          }

          var qry = {
            store: store.store_id,
            table: "items",
            filters: filters,
          };

          let promise = new Promise((resolve, reject) => {
            webserver(
              "get_table",
              qry,
              function (data) {
                resolve(data);
              },
              function () {
                reject([]);
              }
            );
          });

          let data = await promise;

          if (data && data.length > 0) {
            data.forEach((itm) => {
              itm.category =
                window.settings["CATEGORIA"].find((item) => item.codigo == itm.categoria)
                  ?.valor || itm.categoria;

              itm.groupName =
                window.settings["GRUPO"].find((item) => item.codigo == itm.grupo)
                  ?.valor || itm.grupo;

              itm.brand =
                window.settings["MARCA"].find((item) => item.codigo == itm.marca)
                  ?.valor || itm.marca;

              itm.colorName =
                window.settings["COLOR"].find((item) => item.codigo == itm.color)
                  ?.valor || itm.color;

              itm.pos =
                this.stores.find((item) => item.store_id == itm.id_pos)?.store_name ||
                itm.id_pos;

              // Populate available colors and sizes
              if (!this.availableColors.includes(itm.colorName)) {
                this.availableColors.push(itm.colorName);
              }
              if (!this.availableSizes.includes(itm.talla)) {
                this.availableSizes.push(itm.talla);
              }
            });

            response.push.apply(response, data);
          }
        }

        this.items = response;
        this.originalItems = [...response];

        // Update available options
        this.availableColors = [
          ...new Set(this.originalItems.map((item) => item.colorName)),
        ].sort();

        this.availableSizes = [
          ...new Set(this.originalItems.map((item) => item.talla)),
        ].sort();

        // Prepare chart data
        this.prepareChartData();

        this.showMessage(
          `${response.length} productos encontrados en ${this.stores.length} tiendas`,
          "success"
        );
      } catch (error) {
        this.showMessage("Error al buscar productos", "error");
        console.error(error);
      } finally {
        this.loading_status = false;
      }
    },

    prepareChartData() {
      if (this.items.length === 0) {
        this.chartData = null;
        return;
      }

      // Group by store
      const storeData = {};
      this.items.forEach((item) => {
        if (!storeData[item.pos]) {
          storeData[item.pos] = 0;
        }
        storeData[item.pos] += parseFloat(item.cantidad) || 0;
      });

      // Convert to chart format
      const chartArray = [["Tienda", "Cantidad"]];
      Object.entries(storeData).forEach(([store, quantity]) => {
        chartArray.push([store, quantity]);
      });

      this.chartData = chartArray;
    },

    addFilter(type) {
      this.dynamicFilters.push({ type: type, value: null });
    },

    removeFilter(index) {
      this.dynamicFilters.splice(index, 1);
      this.applyFilters();
    },

    resetFilters() {
      this.selectedColor = null;
      this.selectedSize = null;
      this.items = [...this.originalItems];
    },

    applyFilters() {
      this.items = this.originalItems.filter((item) => {
        const colorMatch = !this.selectedColor || item.colorName === this.selectedColor;
        const sizeMatch = !this.selectedSize || item.talla === this.selectedSize;
        return colorMatch && sizeMatch;
      });

      this.prepareChartData();

      const filterCount = (this.selectedColor ? 1 : 0) + (this.selectedSize ? 1 : 0);
      if (filterCount > 0) {
        this.showMessage(
          `${this.items.length} productos después de aplicar ${filterCount} filtro(s)`,
          "info"
        );
      }
    },

    get_stores() {
      var qry = {
        store: window.store.store_id,
        table: "stores",
        filters: [],
      };

      qry.filters.push({
        field: "store_owner",
        operator: "=",
        value: window.store.store_id.substring(0, 6),
      });

      this.loading_status = true;

      webserver(
        "get_table",
        qry,
        (data) => {
          this.stores = data || [];
          this.loading_status = false;

          if (this.stores.length === 0) {
            this.showMessage("No se encontraron tiendas disponibles", "warning");
          }
        },
        () => {
          this.loading_status = false;
          this.showMessage("Error al cargar las tiendas", "error");
        }
      );
    },

    GetSortOrder(prop) {
      return function (a, b) {
        if (a[prop] > b[prop]) {
          return 1;
        } else if (a[prop] < b[prop]) {
          return -1;
        }
        return 0;
      };
    },
  },
};
</script>

<style scoped>
/* KPI Cards */
.kpi-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon .v-icon {
  font-size: 28px !important;
}

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Main Card */
.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

/* Data Table */
.minimal-table {
  border: none !important;
}

.minimal-table >>> .v-data-table__wrapper {
  border: none;
}

.minimal-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.875rem !important;
  padding: 16px !important;
}

.minimal-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0 !important;
  transition: background-color 0.2s;
}

.minimal-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.minimal-table >>> tbody tr td {
  padding: 16px !important;
  font-size: 0.875rem !important;
}

/* Custom cells */
.store-info {
  display: flex;
  align-items: center;
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.product-main {
  display: flex;
  align-items: center;
  gap: 8px;
}

.variant-info {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.quantity-info {
  font-size: 1rem;
}

/* Chart Container */
.chart-container {
  min-height: 400px;
  width: 100%;
}

/* Loading */
.minimal-table >>> .v-data-table__progress {
  height: 3px !important;
}

/* Responsive */
@media (max-width: 600px) {
  .kpi-value {
    font-size: 1.25rem;
  }

  .section-title {
    font-size: 1.125rem;
  }

  .kpi-card .v-card__text {
    flex-direction: column;
    text-align: center;
    padding: 16px !important;
  }

  .kpi-icon {
    margin-bottom: 8px;
  }

  .minimal-toolbar {
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px !important;
  }

  .chart-container {
    min-height: 300px;
  }
}

@media (max-width: 960px) {
  .kpi-value {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .kpi-card .v-card__text {
    flex-direction: row;
    text-align: left;
  }
}
</style>
